'use client';

import { useState } from 'react';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { FormField } from '@/components/ui/form/form-field';
import { MessageBar } from '@/components/ui/message-bar';
import { useAuthTransition } from '@/lib/auth/auth-transition';
import { handoffUrl } from '@/lib/auth/legacy-handoff';
import { postWithRegionRetry } from '@/lib/auth/post-with-region-retry';
import styles from './login-form.module.css';

const LoginSchema = z.object({
  email: z.string().email('Please enter a valid email'),
  password: z.string().min(1, 'Password is required'),
});
type LoginValues = z.infer<typeof LoginSchema>;

export function LoginForm() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const movedNotice = searchParams.get('notice') === 'moved';
  const { playLogin } = useAuthTransition();
  const [serverError, setServerError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isValid },
  } = useForm<LoginValues>({
    resolver: zodResolver(LoginSchema),
    defaultValues: { email: '', password: '' },
    // onChange so `isValid` is live and the button can gate on it, matching
    // create-portfolio/create-initiative.
    mode: 'onChange',
  });

  const onSubmit = handleSubmit(async (values) => {
    setServerError(null);
    const res = await postWithRegionRetry('/web-api/auth/login', values);
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      setServerError(body.message ?? 'Login failed');
      return;
    }
    const body = (await res.json().catch(() => ({}))) as { platform?: string };
    if (body.platform === 'legacy') {
      window.location.assign(handoffUrl(searchParams.get('next')));
      return;
    }
    await playLogin(async () => {
      // Honor a safe internal deep-link, else hand off to `/`, which picks the
      // landing route from the user's role (superAdmins get the admin control
      // panel). Never bounce back to an auth page (e.g. next=/auth/login),
      // which would loop.
      const next = searchParams.get('next');
      const isSafeNext =
        !!next && next.startsWith('/') && !next.startsWith('/login') && !next.startsWith('/auth');
      router.push(isSafeNext ? next : '/');
      router.refresh();
    });
  });

  return (
    <section className={styles.formBody}>
      <div className={styles.formTitle}>
        <h1 className={styles.formTitleHeading}>Welcome to ChangePlan</h1>
        <p className={styles.formSubtitle}>To get started, please log in</p>
        {movedNotice ? (
          <MessageBar variant="info" shape="note" icon="info">
            Your workspace has moved. Please sign in again.
          </MessageBar>
        ) : null}
      </div>

      <Button href="/auth/sso" size="medium" color="secondary" className={styles.loginSso}>
        Login via SSO
      </Button>

      <div className={styles.formDivider}>
        <span className={styles.formDividerText}>OR</span>
      </div>

      <form onSubmit={onSubmit} noValidate className={styles.loginForm}>
        <div className={styles.formInputs}>
          <FormField
            className={styles.formField}
            hasError={Boolean(errors.email)}
            error={errors.email?.message}
            label="Email address"
          >
            <input
              id="email"
              type="email"
              autoComplete="email"
              placeholder="Email address..."
              aria-label="Email address"
              className={styles.formInput}
              {...register('email')}
            />
          </FormField>
          <FormField
            className={styles.formField}
            hasError={Boolean(errors.password)}
            error={errors.password?.message}
            label="Password"
            iconEnd={showPassword ? 'eye' : 'eye-closed'}
            iconEndClickable
            iconEndDisabled={isSubmitting}
            onIconEndClick={() => setShowPassword((value) => !value)}
          >
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              placeholder="Password..."
              aria-label="Password"
              className={styles.formInput}
              {...register('password')}
            />
          </FormField>
        </div>
        {serverError ? (
          <div role="alert" className={styles.serverError}>
            <MessageBar variant="negative" shape="note" icon="triangle-alert">
              {serverError}
            </MessageBar>
          </div>
        ) : null}
        <Button
          type="submit"
          size="medium"
          isLoading={isSubmitting}
          disabled={isSubmitting || !isValid}
          className={styles.loginBtn}
        >
          {isSubmitting ? 'Logging in...' : 'Login'}
        </Button>
      </form>

      <Link className={styles.forgotPassword} href="/auth/forgot-password">
        Forgot Password?
      </Link>
    </section>
  );
}
